import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * useUrlState — keeps a single piece of state in sync with a URL search param.
 * Replace-only navigation (no extra history entries). Reads URL on first mount;
 * writes back when value changes. Empty/default-equivalent values are removed
 * from the URL so links stay clean.
 *
 * @param {string} paramKey   The URL search param key (e.g. 'brand', 'stores')
 * @param {*} defaultValue    Value used when the param is missing/empty
 * @param {object} opts
 *   - parse(rawString)   : convert URL string -> in-app value (default: identity)
 *   - serialize(value)   : convert value -> URL string (default: String)
 *   - isDefault(value)   : if true, the param is removed from the URL
 */
export function useUrlState(paramKey, defaultValue, opts = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const parse = opts.parse || ((s) => s);
  const serialize = opts.serialize || ((v) => String(v));
  const isDefault = opts.isDefault || ((v) => v === defaultValue);

  const [value, setValue] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get(paramKey);
      if (raw == null || raw === '') return defaultValue;
      return parse(raw);
    } catch {
      return defaultValue;
    }
  });

  // Avoid pushing back into URL on the initial mount (we just read from it).
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      const sp = new URLSearchParams(window.location.search);
      if (isDefault(value)) {
        sp.delete(paramKey);
      } else {
        sp.set(paramKey, serialize(value));
      }
      const newSearch = sp.toString();
      const nextSearch = newSearch ? `?${newSearch}` : '';
      if (nextSearch !== window.location.search) {
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      }
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return [value, setValue];
}
